import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ComponentType<any>;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  className?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, className = '' }) => {
  const location = useLocation();
  const { t } = useLanguage();

  // Auto-generate breadcrumbs from current path if no items provided
  const getBreadcrumbsFromPath = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: t('home') || '首頁', href: '/', icon: Home }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Generate human-readable labels
      let label = segment;
      switch (segment) {
        case 'dashboard':
          label = t('dashboard') || '儀表板';
          break;
        case 'company-selection':
          label = t('selectCompany') || '公司選擇';
          break;
        case 'company-management':
          label = t('companyManagement') || '公司管理';
          break;
        case 'product-overview':
          label = t('productOverview') || '產品總覽';
          break;
        case 'management':
          label = t('productMgmt') || '產品管理';
          break;
        case 'stock':
          label = t('inventoryMgmt') || '庫存管理';
          break;
        case 'edit':
          label = '編輯';
          break;
        case 'login':
          label = '登入';
          break;
        case 'register':
          label = '註冊';
          break;
        case 'reset-password':
          label = '重設密碼';
          break;
        default:
          // Capitalize first letter and replace hyphens with spaces
          label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      }

      breadcrumbs.push({
        label,
        href: index === pathSegments.length - 1 ? undefined : currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || getBreadcrumbsFromPath();

  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <nav 
      className={`flex items-center space-x-1 text-sm text-slate-400 ${className}`}
      aria-label="麵包屑導航"
    >
      {breadcrumbItems.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <ChevronRight className="w-4 h-4 text-slate-500" />
          )}
          
          <div className="flex items-center">
            {item.icon && (
              <item.icon className="w-4 h-4 mr-1" />
            )}
            
            {item.href ? (
              <Link
                to={item.href}
                className="hover:text-slate-300 transition-colors duration-200"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-slate-300 font-medium">
                {item.label}
              </span>
            )}
          </div>
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumb;
